<template>
  <div v-if="props.model">
    <div
      :class="[
        isDisabled ? 'question-section-disabled' : 'question-section',
        props.model.isAddAttachmentButtonVisible && 'question-section-with-attachments',
      ]"
    >
      <AttachmentsList
        v-if="attachments.length"
        :model="props.model.attachmentList"
        class="attachments-container"
        :items="attachments"
        mode="composer"
        size="md"
        align="left"
        :show-remove="true"
        :dedupeKey="
          (f: { name: any; size: any; lastModified: any }) =>
            `${f.name}-${f.size}-${f.lastModified}`
        "
        @remove="removeAttachment"
      />

      <div class="question-row">
        <Button
          v-if="props.model.isAddAttachmentButtonVisible"
          :model="{
            path: '',
            base64: appStore.currentMode() === DisplayMode.Light ? iconPlusLight : iconPlusDark,
            url: '',
          }"
          :alt="t('chat.question.addAttachmentButton.alt')"
          :click="openFileDialog"
          :isDisabled="addDisabled"
          :key="componentKey"
          :className="'add-attachment-button'"
          data-cy="add-attachment-button"
        />

        <!-- Hidden native file input -->
        <!-- TODO: Add file predefined file extensions.-->
        <input
          v-if="props.model.isAddAttachmentButtonVisible"
          type="file"
          ref="fileInput"
          class="hidden"
          @change="onFileInput"
          multiple
          data-cy="question-file-input"
        />

        <Input
          :model="t('chat.question.placeholder')"
          v-model="inputValue"
          @input-value-changed="handleInputChange"
          @enter-key-pressed="handleEnterKeyPressed"
          :isDisabled="isDisabled"
          :key="componentKey"
          ref="questionInput"
          :className="'question-input'"
          :id="'question-input'"
          data-cy="question-input"
        />

        <div
          v-if="isInputFilledIn || chatStore.isFirstQuestionSent"
          :class="
            isDisabled || (chatStore.isFirstQuestionSent && !isInputFilledIn)
              ? 'send-question-container-disabled'
              : 'send-question-container-enabled'
          "
          @click="handleButtonClick"
        >
          <Button
            :model="{
              path: '',
              base64:
                appStore.currentMode() === DisplayMode.Light ? iconArrowUpLight : iconArrowUpDark,
              url: '',
            }"
            :alt="t('chat.question.sendQuestionButton.alt')"
            :isDisabled="isDisabled"
            :key="componentKey"
            :className="'send-question-button'"
          />
        </div>
      </div>

      <div class="attachments-status" v-if="attachments.length">
        <span>
          {{ t('chat.question.remainingAttachments') }}{{ count }}/{{
            props.model.attachmentLimitations.maximumAttachments
          }}
          • {{ t('chat.question.remainingFileSize') }}{{ readableBytes(totalBytes) }}/{{
            readableBytes(props.model.attachmentLimitations.maximumBytesTotal)
          }}
        </span>
        <!-- <span v-if="attachError" class="attachments-error">{{ attachError }}</span> -->
      </div>
    </div>

    <PredefinedPromptModifiers
      v-if="!chatStore.isFirstQuestionSent"
      :model="props.model?.predefinedPromptModifiers"
      @prompt-selected="enterPredefinedPromptModifier"
      :key="componentKey"
    />
  </div>
</template>

<script setup lang="ts">
import type { Question } from '../../types/configuration/Question'
import Input from '../Input.vue'
import Button from '../Button.vue'
import { t } from '../../composables/Translation'
import { updateComponentKey } from '../../composables/ComponentKey'
import { nextTick, ref, watch } from 'vue'
import {
  askQuestionAsync,
  addFilesForUpload,
  removeFileFromUploadList,
} from '../../composables/ChatCommunication'
import PredefinedPromptModifiers from './PredefinedPromptModifiers.vue'
import AttachmentsList from '../AttachmentsList.vue'
import { useChatStore } from '../../stores/chatStore'
import { useAidaSessionStore } from '../../stores/aidaSessionStore'
import { useAttachments } from '../../composables/AttachmentsValidation'
import type { MessageAttachment } from '../../types/chat/MessageAttachment'
import iconPlusLight from '../../assets/icons/light/icon-plus.svg'
import iconPlusDark from '../../assets/icons/dark/icon-plus.svg'
import iconArrowUpLight from '../../assets/icons/light/icon-arrow-up.svg'
import iconArrowUpDark from '../../assets/icons/dark/icon-arrow-up.svg'
import { DisplayMode } from '../../types/app/displayMode'
import { useAppStore } from '../../stores/appStore'
import { useToast } from '../../composables/useToast'

const appStore = useAppStore()

const aidaSessionStore = useAidaSessionStore()
const questionInput = ref<HTMLInputElement | null>(null)

const props = defineProps<{
  model: Question | undefined
}>()

const emit = defineEmits<{
  (e: 'answer-received', answer: string): void
  (e: 'question-sent', question: string, attachments?: MessageAttachment[]): void
  (e: 'answer-error', message?: string): void
}>()

let componentKey = updateComponentKey(props)
const inputValue = ref('')
const isDisabled = ref(false)
const chatStore = useChatStore()
const isInputFilledIn = ref(false)

const fileInput = ref<HTMLInputElement | null>(null)

const {
  attachments,
  totalBytes,
  count,
  addDisabled,
  error: attachError,
  readableBytes,
  tryAdd,
  clear,
  removeByKey,
} = useAttachments({
  maximumAttachments: props.model?.attachmentLimitations.maximumAttachments,
  maximumBytesTotal: props.model?.attachmentLimitations.maximumBytesTotal,
  maximumBytesPerFile: props.model?.attachmentLimitations.maximumBytesPerFile,
  acceptedExtensions: props.model?.attachmentLimitations.acceptedExtensions,
  acceptedMime: props.model?.attachmentLimitations.acceptedMime,
})

function openFileDialog() {
  fileInput.value?.click()
}

function onFileInput(e: Event) {
  const input = e.target as HTMLInputElement
  const files = input.files
  if (!files) return

  const { accepted } = tryAdd(files)

  if (accepted.length > 0) {
    const aida = aidaSessionStore.aidaLibrary
    addFilesForUpload(aida, accepted)
  }

  input.value = ''
}

function removeAttachment(key: string, name: string) {
  const aida = aidaSessionStore.aidaLibrary
  removeFileFromUploadList(aida, name)
  removeByKey(key)
}

function handleInputChange(val: string) {
  inputValue.value = val
  isInputFilledIn.value = val !== '' && val.length > 0
}

function enterPredefinedPromptModifier(text: string) {
  handleInputChange(text)
  handleButtonClick()
}

function handleEnterKeyPressed() {
  handleButtonClick()
}

async function handleButtonClick() {
  isDisabled.value = true

  if (inputValue.value && inputValue.value.length > 0) {
    isDisabled.value = true
    const inputValueCopy = inputValue.value
    handleInputChange('')
    if (!chatStore.isFirstQuestionSent) {
      chatStore.setIsFirstQuestionSent(true)
    }

    emit('question-sent', inputValueCopy, attachments.value)
    clear()

    const aida = aidaSessionStore.aidaLibrary
    try {
      if (!aida) {
        emit('answer-error')
      }

      const answer = await askQuestionAsync(aida, inputValueCopy)

      if (answer.answer) {
        emit('answer-received', answer.answer)
      } else {
        emit('answer-error', answer.error)
      }
    } catch (error: any) {
      console.error(error)
      emit('answer-error')
    }
  }
  isDisabled.value = false
  await nextTick()
  questionInput.value?.focus()
}

watch(attachError, (newVal, oldVal) => {
  if (newVal && newVal !== oldVal) {
    useToast().error(newVal)
    attachError.value = ''
  }
})
</script>

<style scoped lang="scss">
.hidden {
  display: none;
}

.attachments-status {
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 14px;
  color: var(--light-color);
  width: 100%;
  height: 100%;
  text-align: center;
}

.attachments-container {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  padding-bottom: 6px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}

.question-section,
.question-section-disabled {
  display: flex;
  padding: 8px 16px;
  justify-content: space-between;
  align-items: center;
  align-self: stretch;
  border-radius: 25px;
  border: 1px solid var(--border-color);
  gap: 24px;
}

.question-section-disabled {
  background-color: light-dark(rgba(239, 239, 239, 0.3), rgba(59, 59, 59, 0.3));
}

:deep(.button.add-attachment-button),
:deep(.button-disabled.add-attachment-button) {
  height: 16px;
  width: 16px;
  flex-shrink: 0;
}

:deep(.input.question-input),
:deep(.input-disabled.question-input) {
  flex: 1;
}

:deep(.input.question-input input),
:deep(.input-disabled.question-input input) {
  width: 97%;
  padding: 4px 0;
  box-sizing: border-box;
  border: var(--background-color);
  color: var(--text-color);
  font-weight: 400;
  line-height: 150%; /* 24px */
}

:deep(.input-disabled.question-input input) {
  background-color: inherit;
}

:deep(.input.question-input input:focus) {
  border: none;
  outline: none;
}

:deep(.input.question-input input::placeholder),
:deep(.input-disabled.question-input input::placeholder) {
  color: var(--light-color);
  font-weight: 500;
}

:deep(.input.question-input input:-webkit-autofill),
:deep(.input-disabled.question-input input:-webkit-autofill) {
  box-shadow: 0 0 0px 1000px var(--background-color) inset; /* Overrides background */
  -webkit-text-fill-color: #000; /* Change text color */
  font-size: 16px !important;
}

.send-question-container-enabled,
.send-question-container-disabled {
  display: flex;
  width: 32px;
  height: 32px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
  border-radius: 25px;
  cursor: pointer;
}

.send-question-container-enabled {
  background: var(--primary-color);
}

.send-question-container-disabled {
  background: var(--text-background-color);
}

:deep(.button.send-question-button) {
  display: flex;
  padding: 4px;
  justify-content: center;
  align-items: center;
  gap: 10px;
}

.question-section-with-attachments {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  padding: 8px 16px;
  gap: 8px;
}

.question-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 24px;
}

.question-section-with-attachments .attachments-container {
  border-bottom: none;
  padding-bottom: 4px;
}

@media (max-width: 992px) {
  .question-with-footer-section .question-section,
  .question-section-disabled {
    width: auto;
    margin: 0px;
  }

  .sticky-question-with-footer-section .question-section,
  .question-section-disabled {
    width: auto;
    margin-left: 12px;
    margin-right: 12px;
  }
}
</style>
